/**
 * Content and release identifiers shared by server code, client components and
 * the request proxy. Free of `server-only` and of Node builtins.
 */

import { ECHOENGINE, ECHOPRINT, type PortalProduct, type ProductId } from '../portal/product';

export const CONTENT_LOCALES = ['en', 'zh-Hans'] as const;
export type ContentLocale = (typeof CONTENT_LOCALES)[number];

export const DEFAULT_CONTENT_LOCALE: ContentLocale = 'en';

export const LOCALE_LABELS: Record<ContentLocale, { languageTag: string; displayName: string; nativeName: string }> = {
  en: { languageTag: 'en', displayName: 'English', nativeName: 'EN' },
  'zh-Hans': { languageTag: 'zh-Hans', displayName: 'Simplified Chinese', nativeName: '中文' },
};

/**
 * Document ids, per product.
 *
 * The two products have genuinely different document sets — EchoPrint publishes
 * one end-user guide, Echo Engine publishes a topic per engine subsystem — and
 * both sets will grow. They are kept as separate literal unions rather than one
 * widened `string` because the ids are Firestore document ids and are indexed
 * directly by the admin console: `DOCUMENT_LABELS[id]` and
 * `documentState(USER_GUIDE_DOCUMENT_ID)` are only checkable while the union is
 * exact. `ContentDocumentId` therefore keeps meaning "an EchoPrint document",
 * which is what every existing call site already means by it, and shared library
 * code that must accept either product takes `AnyContentDocumentId`.
 */
export const CONTENT_DOCUMENT_IDS = ['user-guide', 'agent-instructions'] as const;
export type ContentDocumentId = (typeof CONTENT_DOCUMENT_IDS)[number];

/**
 * Ordered by the reader-facing table of contents, which is the upstream
 * manifest's `order`. `agent-instructions` is last: it is a behaviour contract
 * for a model, not a chapter.
 */
export const ECHOENGINE_DOCUMENT_IDS = [
  'architecture',
  'service-lifecycle',
  'core-services',
  'scene-service',
  'resource-service',
  'shader-service',
  'render-service',
  'compute-service',
  'input-and-collision-services',
  'geometry-and-animation',
  'puppy-scene-walkthrough',
  'character-and-animation',
  'sticker-service',
  'robot-demo',
  'spatial-sound',
  'developer-workflow',
  'security-boundary',
  'release-packaging',
  'agent-instructions',
] as const;
export type EchoengineDocumentId = (typeof ECHOENGINE_DOCUMENT_IDS)[number];

export type AnyContentDocumentId = ContentDocumentId | EchoengineDocumentId;

export const USER_GUIDE_DOCUMENT_ID: ContentDocumentId = 'user-guide';
export const AGENT_DOCUMENT_ID: ContentDocumentId = 'agent-instructions';

export interface ContentDocumentDescriptor {
  id: AnyContentDocumentId;
  /** Position in the reader-facing table of contents. */
  order: number;
  /**
   * Bilingual title. A locale may be absent, in which case the bundler falls
   * back to the document's own `# ` heading — which is how the English-only
   * agent contract gets its title without restating it here.
   */
  titles: Partial<Record<ContentLocale, string>>;
  /** False for documents that exist in the default locale only. */
  localized: boolean;
  /** False for documents a reader is never sent to read. */
  publishable: boolean;
}

export interface ProductContentRegistry {
  productId: ProductId;
  /** Every document this product has, in table-of-contents order. */
  documentIds: readonly AnyContentDocumentId[];
  /** The document a reader lands on first. */
  primaryDocumentId: AnyContentDocumentId;
  /**
   * The behaviour contract for an AI agent. Never part of the readable corpus
   * and never cited, so it is named here rather than inferred from the list.
   */
  agentDocumentId: AnyContentDocumentId;
  documents: readonly ContentDocumentDescriptor[];
  /**
   * Namespace applied to every `id` inside rendered documentation, and to every
   * in-document link, so that authored anchors cannot clobber DOM globals. The
   * value is also the sanitizer's clobber prefix, which is what actually applies
   * it — see `markdown.ts`. EchoPrint's value is live in published links and is
   * frozen.
   */
  anchorPrefix: string;
  /** Directory under the repository root holding this product's authored content. */
  contentDirectory: string;
}

const ECHOPRINT_REGISTRY: ProductContentRegistry = {
  productId: 'echoprint',
  documentIds: CONTENT_DOCUMENT_IDS,
  primaryDocumentId: 'user-guide',
  agentDocumentId: 'agent-instructions',
  documents: [
    {
      id: 'user-guide',
      order: 1,
      titles: { en: 'EchoPrint User Guide', 'zh-Hans': 'EchoPrint 用户指南' },
      localized: true,
      publishable: true,
    },
    { id: 'agent-instructions', order: 2, titles: {}, localized: false, publishable: false },
  ],
  anchorPrefix: 'echoprint-doc-',
  contentDirectory: 'echoprint',
};

const ECHOENGINE_REGISTRY: ProductContentRegistry = {
  productId: 'echoengine',
  documentIds: ECHOENGINE_DOCUMENT_IDS,
  primaryDocumentId: 'architecture',
  agentDocumentId: 'agent-instructions',
  documents: [
    {
      id: 'architecture',
      order: 1,
      titles: { en: 'Architecture overview', 'zh-Hans': '架构概览' },
      localized: true,
      publishable: true,
    },
    {
      id: 'service-lifecycle',
      order: 2,
      titles: { en: 'Service lifecycle', 'zh-Hans': '服务生命周期' },
      localized: true,
      publishable: true,
    },
    {
      id: 'core-services',
      order: 3,
      titles: { en: 'Core services and responsibilities', 'zh-Hans': '核心服务与职责' },
      localized: true,
      publishable: true,
    },
    {
      id: 'scene-service',
      order: 4,
      titles: { en: 'SceneService and the scene factory', 'zh-Hans': 'SceneService 与场景工厂' },
      localized: true,
      publishable: true,
    },
    {
      id: 'resource-service',
      order: 5,
      titles: { en: 'ResourceService — asset locations, formats, and caching', 'zh-Hans': 'ResourceService — 资源位置、格式与缓存' },
      localized: true,
      publishable: true,
    },
    {
      id: 'shader-service',
      order: 6,
      titles: { en: 'ShaderService — shader discovery, includes, and compilation', 'zh-Hans': 'ShaderService — 着色器发现、包含文件与编译' },
      localized: true,
      publishable: true,
    },
    {
      id: 'render-service',
      order: 7,
      titles: { en: 'RenderService, RenderObject, and RenderPipeline', 'zh-Hans': 'RenderService、RenderObject 与 RenderPipeline' },
      localized: true,
      publishable: true,
    },
    {
      id: 'compute-service',
      order: 8,
      titles: { en: 'ComputeService, ComputePipeline, and GPU animation', 'zh-Hans': 'ComputeService、ComputePipeline 与 GPU 动画' },
      localized: true,
      publishable: true,
    },
    {
      id: 'input-and-collision-services',
      order: 9,
      titles: { en: 'InputService and CollisionService', 'zh-Hans': 'InputService 与 CollisionService' },
      localized: true,
      publishable: true,
    },
    {
      id: 'geometry-and-animation',
      order: 10,
      titles: { en: 'Geometry, Animation, and SceneObject composition', 'zh-Hans': 'Geometry、Animation 与 SceneObject 组合' },
      localized: true,
      publishable: true,
    },
    {
      id: 'puppy-scene-walkthrough',
      order: 11,
      titles: { en: 'Puppy scene walkthrough and the rigged-character recipe', 'zh-Hans': 'Puppy 场景全流程与刚性角色配方' },
      localized: true,
      publishable: true,
    },
    {
      id: 'character-and-animation',
      order: 11.5,
      titles: { en: 'Character and GPU-skinned animation', 'zh-Hans': 'Character 与 GPU 蒙皮动画' },
      localized: true,
      publishable: true,
    },
    {
      id: 'sticker-service',
      order: 12,
      titles: { en: 'StickerService usage', 'zh-Hans': 'StickerService 用法' },
      localized: true,
      publishable: true,
    },
    {
      id: 'robot-demo',
      order: 13,
      titles: {
        en: 'Robot demo — stickers in a complete application',
        'zh-Hans': '机器人演示 — 完整应用中的贴纸',
      },
      localized: true,
      publishable: true,
    },
    {
      id: 'spatial-sound',
      order: 14,
      titles: { en: 'Spatial sound', 'zh-Hans': '空间音效' },
      localized: true,
      publishable: true,
    },
    {
      id: 'developer-workflow',
      order: 15,
      titles: { en: 'Developer workflow', 'zh-Hans': '开发者工作流' },
      localized: true,
      publishable: true,
    },
    {
      id: 'security-boundary',
      order: 16,
      titles: { en: 'Security boundary', 'zh-Hans': '安全边界' },
      localized: true,
      publishable: true,
    },
    {
      id: 'release-packaging',
      order: 17,
      titles: { en: 'Release packaging', 'zh-Hans': '发布打包' },
      localized: true,
      publishable: true,
    },
    {
      id: 'agent-instructions',
      order: 18,
      titles: { en: 'Echo Engine developer assistant instructions' },
      localized: false,
      publishable: false,
    },
  ],
  anchorPrefix: 'echoengine-doc-',
  contentDirectory: 'echoengine',
};

export const CONTENT_REGISTRIES: Record<ProductId, ProductContentRegistry> = {
  echoprint: ECHOPRINT_REGISTRY,
  echoengine: ECHOENGINE_REGISTRY,
};

export function contentRegistry(product: PortalProduct = ECHOPRINT): ProductContentRegistry {
  return CONTENT_REGISTRIES[product.id];
}

export function contentDocumentIds(product: PortalProduct = ECHOPRINT): readonly AnyContentDocumentId[] {
  return contentRegistry(product).documentIds;
}

export function contentDocument(
  documentId: AnyContentDocumentId,
  product: PortalProduct = ECHOPRINT,
): ContentDocumentDescriptor | null {
  return contentRegistry(product).documents.find((document) => document.id === documentId) ?? null;
}

export function agentDocumentId(product: PortalProduct = ECHOPRINT): AnyContentDocumentId {
  return contentRegistry(product).agentDocumentId;
}

export function anchorPrefix(product: PortalProduct = ECHOPRINT): string {
  return contentRegistry(product).anchorPrefix;
}

export function localeCookieName(product: PortalProduct = ECHOPRINT): string {
  return product.localeCookieName;
}

export const CONTENT_REVISIONS_COLLECTION = ECHOPRINT.collections.contentRevisions;
export const CONTENT_POINTERS_COLLECTION = ECHOPRINT.collections.contentPointers;
export const RELEASES_COLLECTION = ECHOPRINT.collections.releases;
export const RELEASE_POINTERS_COLLECTION = ECHOPRINT.collections.releasePointers;

export const ECHOENGINE_CONTENT_REVISIONS_COLLECTION = ECHOENGINE.collections.contentRevisions;
export const ECHOENGINE_CONTENT_POINTERS_COLLECTION = ECHOENGINE.collections.contentPointers;

/** There is exactly one current-release pointer; the ID is fixed. */
export const CURRENT_RELEASE_POINTER_ID = 'current';

export const LOCALE_PARAM = 'lang';
export const LOCALE_COOKIE_NAME = ECHOPRINT.localeCookieName;

export const ANCHOR_PREFIX = ECHOPRINT_REGISTRY.anchorPrefix;
export const ECHOENGINE_ANCHOR_PREFIX = ECHOENGINE_REGISTRY.anchorPrefix;

export function isContentLocale(value: unknown): value is ContentLocale {
  return typeof value === 'string' && (CONTENT_LOCALES as readonly string[]).includes(value);
}

/**
 * EchoPrint's document ids. Deliberately narrow: the admin console and the
 * EchoPrint routes rely on this predicate to narrow an untrusted query parameter
 * to one of two known Firestore document ids.
 */
export function isContentDocumentId(value: unknown): value is ContentDocumentId {
  return typeof value === 'string' && (CONTENT_DOCUMENT_IDS as readonly string[]).includes(value);
}

export function isEchoengineDocumentId(value: unknown): value is EchoengineDocumentId {
  return typeof value === 'string' && (ECHOENGINE_DOCUMENT_IDS as readonly string[]).includes(value);
}

/**
 * Registry-driven check for library code that is product-agnostic. The predicate
 * widens to `AnyContentDocumentId` because a type guard cannot depend on a
 * runtime argument; route handlers narrow further with the per-product guard.
 */
export function isProductDocumentId(
  value: unknown,
  product: PortalProduct = ECHOPRINT,
): value is AnyContentDocumentId {
  return typeof value === 'string' && (contentDocumentIds(product) as readonly string[]).includes(value);
}
